import * as React from "react";
import { I18n } from "react-redux-i18n";

import {
  SELECT_EMPTY_VALUE,
  Select,
  SelectOption,
  SelectValue,
} from "@modules/fhq-design-tool/components/questionnaire-management/components/select";

import { Title } from "@modules/fhq-design-tool/components/questionnaire-management/components/title";

import { QuestionnairePatientEmailSettings } from "@common/dto/questionnaire/emails-tab";
import { QuestionnaireUpdateLocalDto } from "@modules/fhq-design-tool/dto/questionnaire-update-local-dto";

import { Loading } from "@common/components/loading";
import { Flex } from "@common/components/Flex";
import { QuestionnaireCreateLocalDto } from "@modules/fhq-design-tool/dto/questionnaire-create-local-dto";

import "./patient-email-settings.styles.scss";

type PatientEmailSettingsProps = {
  emailTemplates: Array<SelectOption>;
  questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;
  onChange: (questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => void;
};

export const PatientEmailSettings: React.FC<PatientEmailSettingsProps> = ({
  emailTemplates,
  questionnaire,
  onChange,
}) => {
  const emailTemplatesFormFields = React.useMemo(() => {
    const config: Record<keyof QuestionnairePatientEmailSettings, string> = {
      invitationEmailId: I18n.t("Invitation Email"),
      submitEmailId: I18n.t("Submit Email"),
      unfinishedEmailId: I18n.t("Unfinished Email"),
      reminderEmailId: I18n.t("Reminder Email"),
    };

    return Object.keys(config).map((key) => ({
      label: config[key as keyof QuestionnairePatientEmailSettings],
      name: key,
    }));
  }, []);

  const handleEmailTemplateValueChange = React.useCallback(
    (fieldName: string) => (changedValue: SelectValue | null | undefined) => {
      onChange({
        ...questionnaire,
        emailsTab: {
          ...questionnaire.emailsTab,
          [fieldName]: changedValue,
        },
      });
    },
    [questionnaire, onChange]
  );

  return (
    <div className="FhqDesignToolPatientEmailSettings">
      <Flex>
        <Title margin="0" text={I18n.t("Patient Email Settings")} />
        {!Boolean(emailTemplates.length) && (
          <Loading className="FhqDesignToolPatientEmailSettings__loading" size={20} />
        )}
      </Flex>
      {Boolean(emailTemplates.length) && (
        <div>
          {emailTemplatesFormFields.map((field) => (
            <Select
              clearable
              key={field.name}
              value={questionnaire.emailsTab[field.name as keyof QuestionnairePatientEmailSettings] === 0
                ? SELECT_EMPTY_VALUE
                : questionnaire.emailsTab[field.name as keyof QuestionnairePatientEmailSettings]
              }
              onChange={handleEmailTemplateValueChange(field.name)}
              options={emailTemplates}
              label={field.label}
              labelWidth={125}
              metadata={{
                description: `patient-email-template-setting-${field.name}`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
